import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "../../context/CartContext";
import { AuthContext } from "../../context/AuthContext";
import { useContext } from "react";
import { Search, User, ShoppingBag, Menu, X, Sun, Moon, Loader2 } from "lucide-react";
import { TopBar } from "./TopBar";
import { MegaMenu } from "./MegaMenu";
import { useTheme } from "@/hooks/use-theme";
import { useSamsungSitesMode } from "@/hooks/use-samsung-sites-mode";
import SamsungDarkSitesDialog from "@/components/SamsungDarkSitesDialog";
import logoDark from "@/assets/logo_dark.png";
import logoLight from "@/assets/logo_light.webp";
import { getNavMenus } from "@/api/nav.api";
import { ENDPOINTS, API_URL, resolveAssetUrl, productPath } from "@/config";

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [navMenus, setNavMenus] = useState<any[]>([]);
  const [active, setActive] = useState<any | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { theme, toggle } = useTheme();
  const { isSamsungDarkSites } = useSamsungSitesMode();
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);

  const onThemeClick = () => {
    if (isSamsungDarkSites) {
      setThemeDialogOpen(true);
      return;
    }
    toggle();
  };

  const navigate = useNavigate();
  const { cartItemCount } = useContext(CartContext);
  const { isAuthenticated, customer } = useContext(AuthContext);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const fetchNav = async () => {
      try {
        const res = await getNavMenus();
        if (res.data?.isOk) {
          setNavMenus(res.data.data || []);
        }
      } catch (err) {
        console.error("Failed to fetch nav menus:", err);
      }
    };
    fetchNav();
  }, []);

  // ─── Search autocomplete ────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const debounceRef = useRef<any>(null);
  const abortRef = useRef<AbortController | null>(null);
  const suggestReqIdRef = useRef(0);
  const desktopSearchRef = useRef<HTMLDivElement | null>(null);
  const mobileSearchRef = useRef<HTMLDivElement | null>(null);
  const searchQueryRef = useRef(searchQuery);
  const suggestionsRef = useRef(suggestions);
  searchQueryRef.current = searchQuery;
  suggestionsRef.current = suggestions;

  // Close the dropdown when clicking outside either search box.
  useEffect(() => {
    const onPointerDown = (e: MouseEvent) => {
      const t = e.target as Node;
      const inside =
        !!desktopSearchRef.current?.contains(t) ||
        !!mobileSearchRef.current?.contains(t);
      if (!inside) setShowSuggestions(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  const getImageUrl = (path?: string) => {
    if (!path) return "";
    const p = String(path).replace(/\\/g, "/"); // stored paths use Windows backslashes
    return p.startsWith("http") ? p : `${API_URL}/${p.replace(/^\/+/, "")}`;
  };

  // Debounced + abortable suggestions. Generation id ignores late responses
  // that finish after the user clears/shortens the query.
  const fetchSuggestions = (raw: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = (raw || "").trim();
    const reqId = ++suggestReqIdRef.current;
    if (q.length < 2) {
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = null;
      setSuggestions([]);
      setShowSuggestions(false);
      setSuggestLoading(false);
      return;
    }
    setShowSuggestions(true);
    setSuggestLoading(true);
    debounceRef.current = setTimeout(async () => {
      if (suggestReqIdRef.current !== reqId) return;
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const res = await fetch(`${ENDPOINTS.PRODUCTS.SUGGEST}?q=${encodeURIComponent(q)}&limit=8`, { signal: controller.signal });
        const data = await res.json();
        if (suggestReqIdRef.current !== reqId) return;
        setSuggestions(Array.isArray(data?.data?.products) ? data.data.products : []);
        setShowSuggestions(true);
      } catch (err: any) {
        if (err?.name === "AbortError") return;
        if (suggestReqIdRef.current !== reqId) return;
        setSuggestions([]);
      } finally {
        if (suggestReqIdRef.current === reqId) setSuggestLoading(false);
      }
    }, 250);
  };

  const clearSearch = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    suggestReqIdRef.current += 1;
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;
    setSearchQuery("");
    setSuggestions([]);
    setShowSuggestions(false);
    setSuggestLoading(false);
    setSearchOpen(false);
  };

  // Open suggestions on focus or click (click covers the case where the input
  // already has focus after navigating to a product).
  const openSuggestionsForCurrentQuery = () => {
    const raw = searchQueryRef.current;
    const q = raw.trim();
    if (q.length < 2) return;
    if (suggestionsRef.current.length > 0) {
      setShowSuggestions(true);
      return;
    }
    fetchSuggestions(raw);
  };

  const onSearchChange = (e: any) => {
    setSearchQuery(e.target.value);
    fetchSuggestions(e.target.value);
  };

  const goToSearch = (term?: string) => {
    const q = (term ?? searchQuery).trim();
    setShowSuggestions(false);
    setSearchOpen(false);
    if (term !== undefined) setSearchQuery(term);
    (document.activeElement as HTMLElement | null)?.blur?.();
    navigate(q ? `/products?search=${encodeURIComponent(q)}` : "/products");
  };

  const onSearchKeyDown = (e: any) => {
    if (e.key === "Enter") goToSearch();
  };

  // Keep the query in the bar when opening a product so click can restore results.
  const pickSuggestion = (p: any) => {
    setShowSuggestions(false);
    setSearchOpen(false);
    (document.activeElement as HTMLElement | null)?.blur?.();
    const targetSlug = p.variantSlug || p.variant?.slug || p.slug;
    if (targetSlug) navigate(productPath(targetSlug));
  };

  const openMobileSearch = () => {
    setSearchOpen((open) => {
      const next = !open;
      if (next) {
        // Defer until the mobile panel mounts.
        queueMicrotask(() => openSuggestionsForCurrentQuery());
      }
      return next;
    });
  };

  const renderSuggestions = () => {
    if (!showSuggestions || searchQuery.trim().length < 2) return null;
    return (
      <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-elevated z-50 max-h-[420px] overflow-y-auto">
        {suggestLoading ? (
          <div className="p-4 flex items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Searching…
          </div>
        ) : suggestions.length > 0 ? (
          <>
            {suggestions.map((p, idx) => (
              <button
                key={`${p._id}-${idx}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pickSuggestion(p)}
                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-surface transition-colors border-b border-border/50 last:border-b-0 text-left"
              >
                <div className="w-10 h-10 shrink-0 bg-surface rounded-lg overflow-hidden flex items-center justify-center">
                  {p.image ? (
                    <img src={getImageUrl(p.image)} alt={p.name} className="max-w-full max-h-full object-contain" onError={(e: any) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <Search className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-foreground font-medium line-clamp-2">{p.name}</div>
                  {(p.categoryName || p.brandName) && (
                    <div className="text-xs text-muted-foreground truncate">
                      {[p.categoryName, p.brandName].filter(Boolean).join(" · ")}
                    </div>
                  )}
                </div>
                {typeof p.price === "number" && (
                  <span className="text-sm font-semibold text-primary whitespace-nowrap">KSh {Number(p.price).toLocaleString()}</span>
                )}
              </button>
            ))}
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => goToSearch()}
              className="w-full text-center px-4 py-2.5 text-sm text-primary font-medium hover:bg-surface border-t border-border"
            >
              View all results for "{searchQuery}"
            </button>
          </>
        ) : (
          <div className="p-4 text-center text-sm text-muted-foreground">No products found</div>
        )}
      </div>
    );
  };

  return (
    <header className="sticky top-0 z-50">
      <TopBar />
      <div
        className={`transition-all duration-300 ${
          active?.showDropdown
            ? "bg-card border-b border-primary/20 shadow-sm"
            : scrolled
              ? "glass shadow-elevated"
              : "bg-transparent"
        }`}
        onMouseLeave={() => setActive(null)}
      >
        <div className="container mx-auto flex items-center justify-between px-4 lg:px-8 h-16">
          <Link 
            to="/" 
            className="flex items-center shrink-0" 
            aria-label="Fonexpress home"
            onMouseEnter={() => setActive(null)}
            onClick={clearSearch}
          >
            <img
              src={theme === "dark" ? logoDark : logoLight}
              alt="Fonexpress"
              className="h-9 sm:h-10 w-auto transition-opacity"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navMenus.map((item) => (
              <button
                key={item._id}
                onMouseEnter={() => item.showDropdown ? setActive(item) : setActive(null)}
                onClick={() => navigate(item.path || "/")}
                className={`px-4 py-2 text-sm rounded-lg transition-colors relative ${
                  active?._id === item._id
                    ? "text-primary font-semibold"
                    : "text-muted-foreground font-medium hover:text-primary"
                }`}
              >
                {item.label}
                {active?._id === item._id && (
                  <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 h-0.5 w-10 bg-gradient-brand rounded-full" />
                )}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-1 sm:gap-2" onMouseEnter={() => setActive(null)}>
            <div ref={desktopSearchRef} className="hidden md:flex relative items-center">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary/70 z-10" />
              <input
                value={searchQuery}
                onChange={onSearchChange}
                onKeyDown={onSearchKeyDown}
                onFocus={openSuggestionsForCurrentQuery}
                onClick={openSuggestionsForCurrentQuery}
                placeholder="Search products, brands, categories…"
                className="w-56 lg:w-72 bg-card border border-primary/20 rounded-full pl-9 pr-4 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 focus:w-80 transition-all shadow-sm"
              />
              {renderSuggestions()}
            </div>
            <button
              onClick={onThemeClick}
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              className="p-2.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
            >
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            <SamsungDarkSitesDialog
              open={themeDialogOpen}
              onClose={() => setThemeDialogOpen(false)}
            />
            <button
              onClick={openMobileSearch}
              aria-label="Search"
              className="md:hidden p-2.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
            >
              <Search className="h-5 w-5" />
            </button>
            <button
              onClick={() => navigate(isAuthenticated ? "/account/profile" : "/login")}
              aria-label="Account"
              className="p-2.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
            >
              <User className="h-5 w-5" />
            </button>
            <button
              onClick={() => navigate('/cart')}
              aria-label="Cart"
              className="relative p-2.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
            >
              <ShoppingBag className="h-5 w-5" />
              {cartItemCount > 0 && (
                <span className="absolute top-1 right-1 h-4 min-w-4 px-1 rounded-full bg-gradient-brand text-[10px] font-bold flex items-center justify-center text-primary-foreground">
                  {cartItemCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setMobileOpen((m) => !m)}
              aria-label="Menu"
              className="lg:hidden p-2.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="border-t border-border animate-slide-down">
            <div className="container mx-auto px-4 lg:px-8 py-4">
              <div ref={mobileSearchRef} className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-primary/70 z-10" />
                <input
                  autoFocus
                  value={searchQuery}
                  onChange={onSearchChange}
                  onKeyDown={onSearchKeyDown}
                  onFocus={openSuggestionsForCurrentQuery}
                  onClick={openSuggestionsForCurrentQuery}
                  placeholder="Search products, brands, categories..."
                  className="w-full bg-card border border-primary/20 rounded-xl pl-11 pr-4 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 shadow-sm"
                />
                {renderSuggestions()}
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className="text-muted-foreground">Trending:</span>
                {["MacBook Pro", "iPhone 15", "Sony WH-1000XM5", "PS5"].map((s) => (
                  <button key={s} onClick={() => goToSearch(s)} className="text-foreground hover:text-primary transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <MegaMenu menu={active} />
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-card border-t border-primary/20 animate-slide-down">
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-1">
            {navMenus.map((item) => (
              <Link
                key={item._id}
                to={item.path || "/"}
                onClick={() => setMobileOpen(false)}
                className="px-3 py-2.5 text-sm font-medium rounded-lg text-foreground hover:text-primary hover:bg-primary/5 border-l-2 border-transparent hover:border-primary"
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
